import { isNumber, max, uid } from 'radash';

import { all, ComponentType, Diagram, DiagramComponentInstance, getComponentDefinition } from 'models';

const ComponentInstanceHelpers = {
    generateId: () => {
        return `component-${uid(8)}`;
    },

    getTotalComponents: (componentInstance: Pick<DiagramComponentInstance, 'multiple'>): number => {
        const { multiple } = componentInstance;

        return (multiple?.amount || 1) * (multiple?.series || 1);
    },

    /**
     * Electrical specifications of a port for its configured voltage type, with the `multiple`
     * counts applied so the values describe the whole array instead of a single unit. Mirrors the
     * simulation engine: voltage scales with modules in series, current with units/strings in
     * parallel, power with both. Returns null when the port or its voltage type is not configured.
     */
    normalise: (componentInstance: DiagramComponentInstance, portIndex: number) => {
        const voltageType = componentInstance.configuration.ports?.[portIndex]?.voltageType;
        const port = componentInstance.specifications.electrical.ports?.[portIndex];

        if (!voltageType || !port) return null;

        // @ts-expect-error not every port type carries every voltage type or PV field
        const specifications = port[voltageType] ?? {};

        const series = componentInstance.multiple?.series || 1;
        const parallel = componentInstance.multiple?.amount || 1;

        const scale = (value: unknown, multiplier: number): number | null =>
            isNumber(value) ? value * multiplier : null;

        return {
            voltage: {
                min: scale(specifications.voltage?.min, series),
                nom: scale(specifications.voltage?.nom, series),
                max: scale(specifications.voltage?.max, series),
            },
            current: {
                nom: scale(specifications.current?.nom, parallel),
                max: scale(specifications.current?.max, parallel),
            },
            power: {
                nom: scale(specifications.power?.nom, series * parallel),
                max: scale(specifications.power?.max, series * parallel),
            },
            maximumPowerPointVoltage: scale(specifications.maximumPowerPointVoltage?.value, series),
            maximumPowerPointCurrent: scale(specifications.maximumPowerPointCurrent?.value, parallel),
            openCircuitVoltage: scale(specifications.openCircuitVoltage?.value, series),
            shortCircuitCurrent: scale(specifications.shortCircuitCurrent?.value, parallel),
        };
    },

    generateIndicator: (
        componentInstances: DiagramComponentInstance[],
        componentType: DiagramComponentInstance['componentType'],
    ) => {
        const existingIndicators = componentInstances
            .filter((componentInstance) => componentInstance.componentType === componentType)
            .map((componentInstance) => componentInstance.indicator);

        const highestExistingIndicator = max(existingIndicators) || 0;

        return highestExistingIndicator + 1;
    },

    generateDesignator: (
        componentType: DiagramComponentInstance['componentType'],
        indicator?: number,
        prefix?: string,
    ) => {
        const componentDefinition = ComponentInstanceHelpers.definition(componentType);
        const designatorPrefix = prefix ?? componentDefinition.indicator;

        return indicator ? `${designatorPrefix}-${indicator}` : designatorPrefix;
    },

    extractDesignatorPrefix: (designator: string, componentType: DiagramComponentInstance['componentType']): string => {
        const match = designator.match(/^(.+)-\d+$/);

        if (match) {
            return match[1];
        }

        return ComponentInstanceHelpers.definition(componentType).indicator;
    },

    generateDesignatorSuffix: (componentInstances: DiagramComponentInstance[], prefix: string): number => {
        const existingSuffixes = componentInstances
            .map((componentInstance) => {
                if (!componentInstance.designator.startsWith(`${prefix}-`)) {
                    return null;
                }

                const suffix = componentInstance.designator.slice(prefix.length + 1);

                return /^\d+$/.test(suffix) ? Number(suffix) : null;
            })
            .filter((suffix): suffix is number => suffix !== null);

        return (max(existingSuffixes) || 0) + 1;
    },

    groupByType: (componentInstances: DiagramComponentInstance[]) => {
        // @ts-expect-error we fill this object dynamically
        const grouped: Record<ComponentType, DiagramComponentInstance[]> = {};

        Object.values(all).forEach((definition) => {
            grouped[definition.type] = [];
        });

        componentInstances.forEach((componentInstance) => {
            grouped[componentInstance.componentType].push(componentInstance);
        });

        return grouped;
    },

    componentIsGenerator: (componentInstance: DiagramComponentInstance) => {
        return ['solar', 'wind', 'hydro', 'generator'].includes(componentInstance.componentType);
    },

    componentIsLoad: (componentInstance: DiagramComponentInstance) => {
        return ['load', 'charger', 'light', 'hvac', 'motor'].includes(componentInstance.componentType);
    },

    componentHasPowerField: (componentInstance: DiagramComponentInstance) => {
        return (
            ComponentInstanceHelpers.componentIsGenerator(componentInstance) ||
            ComponentInstanceHelpers.componentIsLoad(componentInstance)
        );
    },

    editable: (componentInstance: DiagramComponentInstance) => {
        return !componentInstance.componentId && componentInstance.componentType !== 'reference';
    },

    searchable: (componentInstance: DiagramComponentInstance) => {
        return ComponentInstanceHelpers.definition(componentInstance).searchable !== false;
    },

    purge: (componInstance: DiagramComponentInstance) => {
        return JSON.parse(JSON.stringify(componInstance)) as DiagramComponentInstance;
    },

    definition: (componentInstanceOrType: DiagramComponentInstance | DiagramComponentInstance['componentType']) => {
        if (typeof componentInstanceOrType === 'string') {
            return getComponentDefinition(componentInstanceOrType);
        }

        return getComponentDefinition(componentInstanceOrType.componentType);
    },

    actsAsConverter: (componentInstance: DiagramComponentInstance) => {
        // @ts-expect-error TODO: type definition needs to get fixed
        if (componentInstance.specifications.electrical?.actsAsConverter) {
            return true;
        }

        return !!ComponentInstanceHelpers.definition(componentInstance).actsAsConverter;
    },

    isGroundingConnectedToComponentInstance: (
        diagram: Diagram,
        componentInstanceId: DiagramComponentInstance['id'],
    ): boolean => {
        return Object.values(diagram.connections).some((connection) => {
            const otherEndpointId =
                connection.from.componentInstanceId === componentInstanceId
                    ? connection.to.componentInstanceId
                    : connection.to.componentInstanceId === componentInstanceId
                      ? connection.from.componentInstanceId
                      : null;

            if (!otherEndpointId) return false;

            return diagram.componentInstances[otherEndpointId]?.componentType === 'grounding';
        });
    },
};

export { ComponentInstanceHelpers };
